import { isInsideCodeSpan } from "../patterns";

export type MarkdownLinkSyntaxKind =
    | "inline"
    | "image"
    | "reference"
    | "autolink"
    | "bare-url";

export interface MarkdownLinkSyntaxMatch {
    fromIndex: number;
    toIndex: number;
    label: string;
    url: string;
    title: string | null;
    raw: string;
    kind: MarkdownLinkSyntaxKind;
}

export interface MarkdownReferenceDefinition {
    id: string;
    url: string;
    title: string | null;
}

export interface MarkdownReferenceDefinitionSyntaxMatch {
    fromIndex: number;
    toIndex: number;
    id: string;
    normalizedId: string;
    url: string;
    title: string | null;
    raw: string;
}

export type MarkdownReferenceDefinitionMap = Map<string, MarkdownReferenceDefinition>;

interface MarkdownDocLike {
    lines: number;
    line(number: number): { text: string };
}

const REFERENCE_DEFINITION_RE = /^[ \t]{0,3}\[([^\]\n]+)\]:[ \t]*(.*)$/;
const URI_AUTOLINK_RE = /<([a-zA-Z][a-zA-Z\d+.-]{1,31}:[^<>\s]+)>/g;
const EMAIL_AUTOLINK_RE = /<([A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)>/g;
const BARE_URL_RE = /(^|[\s([{])((?:https?:\/\/|mailto:)[^\s<>"']+)/g;
const TRAILING_BARE_URL_PUNCT_RE = /[),.;:!?]+$/;

function normalizeReferenceLabel(value: string): string {
    return value.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

function isFootnoteReferenceLabel(value: string): boolean {
    return /^\^[^\s\]]+$/.test(value.trim());
}

function isEscaped(text: string, index: number): boolean {
    let slashCount = 0;
    for (let i = index - 1; i >= 0 && text[i] === "\\"; i -= 1) {
        slashCount += 1;
    }
    return slashCount % 2 === 1;
}

function findClosingBracket(text: string, openIndex: number): number {
    let depth = 0;
    for (let i = openIndex + 1; i < text.length; i += 1) {
        const char = text[i];
        if (isEscaped(text, i)) continue;
        if (char === "[") {
            depth += 1;
            continue;
        }
        if (char === "]") {
            if (depth === 0) return i;
            depth -= 1;
        }
    }
    return -1;
}

function findClosingParen(text: string, openIndex: number): number {
    let depth = 0;
    for (let i = openIndex; i < text.length; i += 1) {
        const char = text[i];
        if (isEscaped(text, i)) continue;
        if (char === "(") {
            depth += 1;
            continue;
        }
        if (char === ")") {
            depth -= 1;
            if (depth === 0) return i;
        }
    }
    return -1;
}

function parseQuotedTitle(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const opener = trimmed[0];
    const closer = opener === "(" ? ")" : opener;
    if (opener !== "\"" && opener !== "'" && opener !== "(") return null;

    let escaped = false;
    for (let i = 1; i < trimmed.length; i += 1) {
        const char = trimmed[i];
        if (escaped) {
            escaped = false;
            continue;
        }
        if (char === "\\") {
            escaped = true;
            continue;
        }
        if (char === closer) {
            return trimmed.slice(1, i);
        }
    }
    return null;
}

function parseDestinationAndTitle(value: string): { url: string; title: string | null } | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("<")) {
        const end = trimmed.indexOf(">");
        if (end <= 0) return null;
        const url = trimmed.slice(1, end).trim();
        if (!url) return null;
        return {
            url,
            title: parseQuotedTitle(trimmed.slice(end + 1)),
        };
    }

    let depth = 0;
    let end = 0;
    for (; end < trimmed.length; end += 1) {
        const char = trimmed[end];
        if (isEscaped(trimmed, end)) continue;
        if (/\s/.test(char) && depth === 0) break;
        if (char === "(") depth += 1;
        if (char === ")" && depth > 0) depth -= 1;
    }

    const url = trimmed.slice(0, end).trim();
    if (!url) return null;
    return {
        url,
        title: parseQuotedTitle(trimmed.slice(end)),
    };
}

function isReferenceDefinitionLine(text: string): boolean {
    return REFERENCE_DEFINITION_RE.test(text);
}

export function parseMarkdownReferenceDefinitionSyntax(
    text: string,
): MarkdownReferenceDefinitionSyntaxMatch | null {
    const match = text.match(REFERENCE_DEFINITION_RE);
    if (!match) return null;
    const id = (match[1] ?? "").trim();
    const normalizedId = normalizeReferenceLabel(id);
    if (isFootnoteReferenceLabel(id)) return null;
    const destination = parseDestinationAndTitle(match[2] ?? "");
    if (!id || !normalizedId || !destination?.url) return null;
    const fromIndex = text.match(/^\s*/)?.[0].length ?? 0;
    return {
        fromIndex,
        toIndex: text.length,
        id,
        normalizedId,
        url: destination.url,
        title: destination.title,
        raw: text.slice(fromIndex),
    };
}

function rangesOverlap(
    left: { fromIndex: number; toIndex: number },
    right: { fromIndex: number; toIndex: number },
): boolean {
    return left.fromIndex < right.toIndex && right.fromIndex < left.toIndex;
}

function rangeOverlapsAny(
    range: { fromIndex: number; toIndex: number },
    ranges: readonly { fromIndex: number; toIndex: number }[],
): boolean {
    return ranges.some((existing) => rangesOverlap(range, existing));
}

function canUseCandidate(
    text: string,
    range: { fromIndex: number; toIndex: number },
    existing: readonly MarkdownLinkSyntaxMatch[],
): boolean {
    if (range.fromIndex >= range.toIndex) return false;
    if (isInsideCodeSpan(text, range.fromIndex)) return false;
    return !rangeOverlapsAny(range, existing);
}

function pushInlineAndReferenceMatches(
    text: string,
    references: MarkdownReferenceDefinitionMap,
    matches: MarkdownLinkSyntaxMatch[],
): void {
    for (let i = 0; i < text.length; i += 1) {
        const image = text[i] === "!" && text[i + 1] === "[";
        const openBracket = image ? i + 1 : i;
        if (text[openBracket] !== "[") continue;
        if (isEscaped(text, openBracket)) continue;
        if (openBracket > 0 && text[openBracket - 1] === "!" && !image) continue;
        if (isInsideCodeSpan(text, i)) continue;

        const closeBracket = findClosingBracket(text, openBracket);
        if (closeBracket < 0) continue;

        const label = text.slice(openBracket + 1, closeBracket).trim();
        const next = text[closeBracket + 1];
        if (next === "(") {
            const closeParen = findClosingParen(text, closeBracket + 1);
            if (closeParen < 0) continue;
            const destination = parseDestinationAndTitle(
                text.slice(closeBracket + 2, closeParen),
            );
            if (!destination?.url) continue;
            matches.push({
                fromIndex: i,
                toIndex: closeParen + 1,
                label,
                url: destination.url,
                title: destination.title,
                raw: text.slice(i, closeParen + 1),
                kind: image ? "image" : "inline",
            });
            i = closeParen;
            continue;
        }

        let refId = "";
        let refEnd = closeBracket;
        let hasExplicitReference = false;
        if (next === "[") {
            const closeRef = findClosingBracket(text, closeBracket + 1);
            if (closeRef < 0) continue;
            refId = text.slice(closeBracket + 2, closeRef).trim() || label;
            refEnd = closeRef;
            hasExplicitReference = true;
        } else {
            refId = label;
        }
        if (isFootnoteReferenceLabel(refId)) continue;

        const definition = references.get(normalizeReferenceLabel(refId));
        if (!definition) continue;
        matches.push({
            fromIndex: i,
            toIndex: refEnd + 1,
            label,
            url: definition.url,
            title: definition.title,
            raw: text.slice(i, refEnd + 1),
            kind: image ? "image" : "reference",
        });
        i = hasExplicitReference ? refEnd : closeBracket;
    }
}

function pushAutolinkMatches(
    text: string,
    matches: MarkdownLinkSyntaxMatch[],
): void {
    URI_AUTOLINK_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URI_AUTOLINK_RE.exec(text)) !== null) {
        const url = match[1] ?? "";
        const candidate = {
            fromIndex: match.index,
            toIndex: match.index + match[0].length,
        };
        if (!url || !canUseCandidate(text, candidate, matches)) continue;
        matches.push({
            ...candidate,
            label: url,
            url,
            title: null,
            raw: match[0],
            kind: "autolink",
        });
    }

    EMAIL_AUTOLINK_RE.lastIndex = 0;
    while ((match = EMAIL_AUTOLINK_RE.exec(text)) !== null) {
        const email = match[1] ?? "";
        const candidate = {
            fromIndex: match.index,
            toIndex: match.index + match[0].length,
        };
        if (!email || !canUseCandidate(text, candidate, matches)) continue;
        matches.push({
            ...candidate,
            label: email,
            url: `mailto:${email}`,
            title: null,
            raw: match[0],
            kind: "autolink",
        });
    }
}

function pushBareUrlMatches(
    text: string,
    matches: MarkdownLinkSyntaxMatch[],
): void {
    BARE_URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = BARE_URL_RE.exec(text)) !== null) {
        const prefix = match[1] ?? "";
        const rawUrl = match[2] ?? "";
        const start = match.index + prefix.length;
        const url = rawUrl.replace(TRAILING_BARE_URL_PUNCT_RE, "");
        if (!url) continue;
        const candidate = {
            fromIndex: start,
            toIndex: start + url.length,
        };
        if (!canUseCandidate(text, candidate, matches)) continue;
        matches.push({
            ...candidate,
            label: url,
            url,
            title: null,
            raw: url,
            kind: "bare-url",
        });
    }
}

export function collectMarkdownReferenceDefinitions(
    doc: MarkdownDocLike,
): MarkdownReferenceDefinitionMap {
    const references: MarkdownReferenceDefinitionMap = new Map();
    for (let lineNumber = 1; lineNumber <= doc.lines; lineNumber += 1) {
        const text = doc.line(lineNumber).text;
        const definition = parseMarkdownReferenceDefinitionSyntax(text);
        if (!definition) continue;
        references.set(definition.normalizedId, {
            id: definition.normalizedId,
            url: definition.url,
            title: definition.title,
        });
    }
    return references;
}

export function findMarkdownLinkSyntaxMatches(
    text: string,
    references: MarkdownReferenceDefinitionMap = new Map(),
): MarkdownLinkSyntaxMatch[] {
    const matches: MarkdownLinkSyntaxMatch[] = [];
    if (isReferenceDefinitionLine(text)) return matches;
    pushInlineAndReferenceMatches(text, references, matches);
    pushAutolinkMatches(text, matches);
    pushBareUrlMatches(text, matches);
    return matches.sort((left, right) =>
        left.fromIndex - right.fromIndex || left.toIndex - right.toIndex,
    );
}

export function rangeContainsMarkdownLink(
    ranges: readonly MarkdownLinkSyntaxMatch[],
    index: number,
): boolean {
    return ranges.some((range) =>
        index >= range.fromIndex && index < range.toIndex,
    );
}
